import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { cities } from "@/lib/data/cities";
import useSEO from "@/hooks/useSEO";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";

const regions = ["Capital Region", "Westman", "Southeast", "Pembina Valley", "Interlake", "Central Plains", "Red River", "Eastman", "Parkland", "Northern"];

// Chamber of commerce and business directory listings (outbound citations).
const chambers = [
  { name: "Winnipeg Chamber of Commerce", url: "https://winnipeg-chamber.com" },
  { name: "Manitoba Chambers of Commerce", url: "https://mbchamber.mb.ca" },
  { name: "Brandon Chamber of Commerce", url: "https://brandonchamber.ca" },
  { name: "Thompson Chamber of Commerce", url: "https://www.thompsonchamber.ca" },
];

export default function Cities() {
  useSEO({
    title: "Security Guards by Manitoba City | Winnipeg, Brandon, Thompson",
    description:
      "Find security guard and patrol options by Manitoba community, including Winnipeg, Brandon, Steinbach and Thompson. Confirm availability locally.",
    keywords: withCanadaKeywords([
      "security companies Winnipeg",
      "hospital security Winnipeg",
      "security guards Brandon",
      "mining security Thompson",
      "mobile patrol Winnipeg",
      ...cities.flatMap((c) => c.keywords),
    ]),
  });

  return (
    <div className="bg-[#050505] pt-32 md:pt-40">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 03
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1
            data-testid="cities-title"
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
          >
            Security coverage{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              across Manitoba communities
            </span>
          </h1>
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Every community in Manitoba is served by Manitoba Security
            Services — from Winnipeg&apos;s downtown financial core to the northern
            fly-in camps of the Thompson nickel corridor. Select a community
            below to review local coverage detail.
          </p>
        </div>
      </section>

      {/* By region */}
      {regions.map((region) => {
        const inRegion = cities.filter((c) => c.region === region);
        if (inRegion.length === 0) return null;
        return (
          <section
            key={region}
            data-testid={`region-${region.replace(/\s+/g, "-").toLowerCase()}`}
            className="border-t border-[#1e1e1e]"
          >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20 grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-3">
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  Region
                </p>
                <h2 className="mt-2 font-display uppercase tracking-tight text-white text-2xl md:text-3xl">
                  {region}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-9">
                <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  {inRegion.map((c, i) => (
                    <motion.li
                      key={c.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{
                        duration: 0.6,
                        delay: i * 0.04,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      <Link
                        to={`/cities/${c.slug}`}
                        data-testid={`cities-list-${c.slug}`}
                        className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                      >
                        <span className="font-display uppercase tracking-tight text-white text-lg md:text-xl group-hover:translate-x-1 transition-transform">
                          {c.name}
                        </span>
                        <ArrowUpRight
                          className="text-white/40 group-hover:text-[#FF3B30] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                          size={18}
                        />
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        );
      })}

      {/* Chamber & business directory listings */}
      <section
        className="border-t border-[#1e1e1e] bg-[#0b0b0b]"
        data-testid="cities-backlinks"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Directories
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display uppercase tracking-tight text-white text-2xl md:text-3xl">
              Chamber &amp; business directory{" "}
              <span className="font-editorial italic font-normal text-[#a0a0a0]">
                listings
              </span>
            </h2>
            <p className="mt-4 font-body text-base text-[#a0a0a0] max-w-2xl leading-relaxed">
              Manitoba Security Services maintains and pursues listings with
              the province&apos;s leading business associations and chamber
              directories — Winnipeg, Brandon, Thompson, and the provincial
              chamber network.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {chambers.map((c) => (
                <li key={c.url}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`backlink-${c.name
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/-$/, "")}`}
                    className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                  >
                    <span className="font-display uppercase tracking-tight text-white text-lg md:text-xl group-hover:translate-x-1 transition-transform">
                      {c.name}
                    </span>
                    <ExternalLink
                      className="text-white/40 group-hover:text-[#FF3B30]"
                      size={16}
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
